import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { requestInstallPrompt } from "@/lib/installPromptBus";
import { enqueue, isNetworkError } from "@/lib/offlineQueue";

// Track whether the user has triggered the "first save" install prompt this
// session so we don't fire it on every subsequent save.
const FIRST_SAVE_FLAG = "atp:first-save-prompted";

export function useSavedEpisodes() {
  const { user } = useAuth();
  const [savedIds, setSavedIds] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (!user) {
      setSavedIds(new Set());
      return;
    }
    supabase
      .from("saved_episodes")
      .select("episode_id")
      .eq("user_id", user.id)
      .then(({ data }) => {
        if (data) setSavedIds(new Set(data.map((r) => r.episode_id)));
      });
  }, [user]);

  // Optimistic toggle: flip local state first, run the network call in the
  // background, and roll back if the DB rejects it. The UI is responsive
  // even on slow networks.
  const toggleSave = useCallback(
    async (episodeId: string) => {
      if (!user) return false;
      const wasSaved = savedIds.has(episodeId);
      const nowSaved = !wasSaved;

      setSavedIds((prev) => {
        const next = new Set(prev);
        if (nowSaved) next.add(episodeId);
        else next.delete(episodeId);
        return next;
      });

      const op = nowSaved
        ? supabase.from("saved_episodes").insert({ user_id: user.id, episode_id: episodeId })
        : supabase.from("saved_episodes").delete().eq("user_id", user.id).eq("episode_id", episodeId);

      const { error } = await op;
      if (error) {
        // Network failure → queue for replay, keep optimistic UI.
        if (isNetworkError(error) || !navigator.onLine) {
          await enqueue({
            kind: "saved_episode",
            payload: { user_id: user.id, episode_id: episodeId, op: nowSaved ? "save" : "unsave" },
            dedupKey: `saved_episode:${user.id}:${episodeId}`,
          });
          toast.message(nowSaved ? "Saved offline — will sync" : "Removed offline — will sync");
          return nowSaved;
        }
        // Real DB rejection — roll back.
        setSavedIds((prev) => {
          const next = new Set(prev);
          if (wasSaved) next.add(episodeId);
          else next.delete(episodeId);
          return next;
        });
        toast.error(nowSaved ? "Couldn't save episode." : "Couldn't remove save.");
        return wasSaved;
      }
      // First successful save → high-intent moment for an install prompt.
      if (nowSaved && typeof window !== "undefined" && !localStorage.getItem(FIRST_SAVE_FLAG)) {
        try { localStorage.setItem(FIRST_SAVE_FLAG, "1"); } catch { /* storage full */ }
        // Defer so the save UI animation lands before the modal opens.
        setTimeout(() => requestInstallPrompt("first-save"), 600);
      }
      return nowSaved;
    },
    [user, savedIds]
  );

  return { savedIds, toggleSave };
}
